
/**
 * Timezone utility functions for consistent time handling across the application
 */

/**
 * Convert a local date and time to UTC ISO string
 * @param date - Local date (YYYY-MM-DD format)
 * @param time - Local time (HH:MM:SS format)
 * @returns UTC ISO string
 */
export const localDateTimeToUTC = (date: string, time: string): string => {
  // Validate inputs
  if (!date || !time) {
    throw new Error(`Invalid date or time: date=${date}, time=${time}`);
  }
  
  // Create a date object in the user's local timezone
  const localDateTime = new Date(`${date}T${time}`);
  
  // Validate the created date
  if (isNaN(localDateTime.getTime())) {
    throw new Error(`Invalid datetime created from: ${date}T${time}`);
  }
  
  // Return as UTC ISO string
  const isoString = localDateTime.toISOString();
  console.log(`TIME CONVERSION: ${date}T${time} (local) -> ${isoString} (UTC)`);
  return isoString;
};

/**
 * Convert UTC booking date and time to ISO string for API calls
 * NOTE: Booking times in database are already in UTC format
 * @param date - UTC date (YYYY-MM-DD format)
 * @param time - UTC time (HH:MM:SS format)
 * @returns UTC ISO string
 */
export const utcBookingTimeToISO = (date: string, time: string): string => {
  // Validate inputs
  if (!date || !time) {
    throw new Error(`Invalid date or time: date=${date}, time=${time}`);
  }
  
  // Create UTC datetime string and parse as UTC
  const utcDateTime = `${date}T${time}Z`;
  const utcDate = new Date(utcDateTime);
  
  // Validate the created date
  if (isNaN(utcDate.getTime())) {
    throw new Error(`Invalid UTC datetime created from: ${utcDateTime}`);
  }
  
  // Return as UTC ISO string
  const isoString = utcDate.toISOString();
  console.log(`UTC BOOKING TIME CONVERSION: ${date}T${time} (UTC) -> ${isoString} (ISO)`);
  return isoString;
};

/**
 * Convert UTC ISO string to local time components
 * @param utcIsoString - UTC ISO string
 * @returns Object with local date and time components
 */
export const utcToLocalDateTime = (utcIsoString: string) => {
  const utcDate = new Date(utcIsoString);
  
  return {
    localDate: utcDate.toLocaleDateString('en-CA'), // YYYY-MM-DD format
    localTime: utcDate.toLocaleTimeString('en-GB', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    }), // HH:MM:SS format
    localTimeDisplay: utcDate.toLocaleTimeString('en-GB', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit'
    }), // HH:MM format for display
    dateTime: utcDate
  };
};

/**
 * Convert a UTC time slot to UTC booking times (for consistent storage)
 * @param timeSlotStart - UTC start time ISO string
 * @param durationMinutes - Duration in minutes
 * @returns UTC start and end times in HH:MM:SS format
 */
export const calculateUTCBookingTimes = (timeSlotStart: string, durationMinutes: number) => {
  const startTime = new Date(timeSlotStart);
  const endTime = new Date(startTime.getTime() + durationMinutes * 60000);
  
  return {
    startTime: startTime.toISOString().split('T')[1].split('.')[0], // Extract HH:MM:SS from ISO string
    endTime: endTime.toISOString().split('T')[1].split('.')[0]
  };
};

/**
 * Convert a UTC time slot to local booking times (for display)
 * @param timeSlotStart - UTC start time ISO string
 * @param durationMinutes - Duration in minutes
 * @returns Local start and end times in HH:MM:SS format
 */
export const calculateLocalBookingTimes = (timeSlotStart: string, durationMinutes: number) => {
  const startTime = new Date(timeSlotStart);
  const endTime = new Date(startTime.getTime() + durationMinutes * 60000);
  
  return {
    startTime: startTime.toLocaleTimeString('en-GB', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    }),
    endTime: endTime.toLocaleTimeString('en-GB', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    })
  };
};

/**
 * Convert local time (HH:MM:SS) to UTC time for a specific date
 * @param date - Date in YYYY-MM-DD format
 * @param time - Time in HH:MM:SS format
 * @returns UTC time in HH:MM:SS format
 */
export const localTimeToUTCTime = (date: string, time: string): string => {
  const localDateTime = new Date(`${date}T${time}`);
  return localDateTime.toUTCString().split(' ')[4]; // Extract HH:MM:SS from UTC string
};

/**
 * Get current date in local timezone (YYYY-MM-DD format)
 */
export const getCurrentLocalDate = (): string => {
  return new Date().toLocaleDateString('en-CA');
};

/**
 * Check if a UTC time is in the past relative to local time
 */
export const isUTCTimeInPast = (utcIsoString: string): boolean => {
  const utcTime = new Date(utcIsoString);
  const now = new Date();
  return utcTime <= now;
};

/**
 * Format a UTC ISO string for display in local timezone
 */
export const formatUTCForDisplay = (utcIsoString: string, options?: Intl.DateTimeFormatOptions): string => {
  const date = new Date(utcIsoString);
  return date.toLocaleString('en-GB', {
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  });
};
